/**
 * Checkout (kiosk pairing) feature — API + config + hooks ported from the
 * standalone pairing-page-ui app so the portal-ui owns the single container.
 *
 * The pair-user endpoint lives on the **davinci-pairing-monitor** service
 * (NOT this management service), so it has its own base URL. In dev we
 * route through a Vite proxy path (`/pair-api/*`) to avoid CORS — see
 * `vite.config.js`. In prod, set `VITE_PAIR_API_BASE_URL` to the real host
 * or proxy it in nginx.
 */
const toBool = (v, fallback) =>
  v === undefined || v === '' ? fallback : String(v).toLowerCase() === 'true';

// Default to a same-origin proxy path in BOTH dev and prod:
//   • dev   — Vite proxy in `vite.config.js` rewrites `/pair-api/*` to the
//             pairing-monitor host (avoids browser CORS).
//   • prod  — nginx in `portal-ui/nginx.conf` proxies
//             `/portal-ui/pair-api/*` to the same host (avoids the 405
//             preflight the pairing service returns for cross-origin OPTIONS).
//
// Browser always sees a same-origin request, so no CORS preflight is fired.
// Override via `VITE_PAIR_API_BASE_URL` if a future env needs a different host.
const isLocalDev =
  typeof window !== 'undefined'
  && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

// In dev Vite is rooted at `/` so `/pair-api/*` works.
// In prod the SPA is served under `/portal-ui/`, so the proxy path must be
// `/portal-ui/pair-api/*` — built dynamically from import.meta.env.BASE_URL.
const PROD_BASE = `${(import.meta.env.BASE_URL || '/').replace(/\/$/, '')}/pair-api`;
const DEFAULT_PAIR_API_BASE_URL = isLocalDev ? '/pair-api' : PROD_BASE;

export const checkoutEnv = {
  apiBaseUrl: (import.meta.env.VITE_PAIR_API_BASE_URL ?? DEFAULT_PAIR_API_BASE_URL).replace(/\/$/, ''),
  // Orgs / sites / employees now come from the real management-service API
  // via the shared apiClient. Flip to `true` only for offline testing.
  useDirectoryMocks: toBool(import.meta.env.VITE_USE_MOCKS, false),
  // pair-user — default OFF so real API is called.
  useMockPairing: toBool(import.meta.env.VITE_USE_MOCK_PAIR, false),
};

